import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { PRIMARY_COLOR } from "./constants";
import { tokens } from "../../theme/tokens";
import SubjectTypeToggle from "../shared/SubjectTypeToggle";
import { SubjectType } from "../../types/CaseTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (firstName: string, lastName: string, subjectType: SubjectType) => void;
}

const AddPoiDialog: React.FC<Props> = ({ open, onClose, onCreate }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [subjectType, setSubjectType] = useState<SubjectType>("person");
  const isBusiness = subjectType === "business";

  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setSubjectType("person");
    }
  }, [open]);

  const canSubmit =
    firstName.trim() !== "" && (isBusiness || lastName.trim() !== "");

  const handleClose = () => onClose();

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreate(firstName.trim(), isBusiness ? "" : lastName.trim(), subjectType);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, pb: "8px" }}>
        Add New Subject / POI
      </DialogTitle>
      <DialogContent
        sx={{
          pt: "8px !important",
          display: "flex",
          flexDirection: "column",
          gap: tokens.space[2],
        }}
      >
        <SubjectTypeToggle value={subjectType} onChange={setSubjectType} />
        <div style={{ display: "flex", gap: tokens.space[2] }}>
          <input
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            placeholder={isBusiness ? "Business name" : "First name"}
            aria-label={isBusiness ? "Business name" : "First name"}
            style={{
              flex: 1,
              fontSize: "13px",
              padding: "8px 10px",
              border: `1px solid ${tokens.color.border.strong}`,
              borderRadius: "7px",
              outline: "none",
              color: tokens.color.text.primary,
            }}
          />
          {!isBusiness && (
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="Last name"
              aria-label="Last name"
              style={{
                flex: 1,
                fontSize: "13px",
                padding: "8px 10px",
                border: `1px solid ${tokens.color.border.strong}`,
                borderRadius: "7px",
                outline: "none",
                color: tokens.color.text.primary,
              }}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: "20px", pb: "16px", gap: "8px" }}>
        <button
          type="button"
          onClick={handleClose}
          style={{
            fontSize: "13px",
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "7px",
            padding: "7px 16px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: tokens.color.surface.default,
            backgroundColor: canSubmit ? PRIMARY_COLOR : tokens.color.text.secondary,
            border: "none",
            borderRadius: "7px",
            padding: "7px 16px",
            cursor: canSubmit ? "pointer" : "default",
          }}
        >
          Add Subject
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPoiDialog;
